import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Feature } from '@assetaccountant/api/models/feature';
import { OrganisationPermissionType } from '@assetaccountant/api/models/organisation-permission-type';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { RegisterDto } from '@assetaccountant/api/models/register.dto';
import { OrganisationV2Api } from '@assetaccountant/api/services/organisation-v2.api';
import { SidePanelModel } from '@assetaccountant/assets/models/side-panel-model';
import { AppNavigationService, Register } from '@assetaccountant/core';
import { CreateRegisterModalComponent } from '@assetaccountant/core/components/navigation/create-register-modal/create-register-modal.component';
import { SidePanelComponent } from '@assetaccountant/core/components/side-panel/side-panel.component';
import { Tag } from '@assetaccountant/core/models/tag';
import { ExportService } from '@assetaccountant/core/services/export.service';
import { ModalService } from '@assetaccountant/core/services/modal.service';
import { OrganisationService } from '@assetaccountant/core/services/organisation.service';
import { filterAndSortByValue } from '@assetaccountant/core/utils/search-utils';
import { TypedNgbModal, setupModal } from '@assetaccountant/core/utils/typed-modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
import { switchMap } from 'rxjs/operators';
import { FilterTagModalComponent } from './filter-tag-modal/filter-tag-modal.component';
import { GenerateConsolidatedReportComponent } from './generate-consolidated-report/generate-consolidated-report.component';

@Component({
  selector: 'app-register-list',
  templateUrl: './register-list.component.html',
  styleUrls: ['./register-list.component.scss'],
  standalone: false,
})
export class RegisterListComponent implements OnChanges {
  readonly PackageFeature = Feature;
  readonly OrganisationPermissionType = OrganisationPermissionType;

  @Input() organisation: OrganisationDto;
  @Input() registers: Array<RegisterDto & { _selected?: boolean; _disabled?: boolean }> = [];
  @Input() demoRegister?: Register;
  @Input() isAllRegistersPage = true;

  @Output() reportRequest = new EventEmitter<string>();

  @ViewChild('sidePanel') sidePanel: SidePanelComponent;

  search = '';

  selectedTags: string[] = [];
  tags: Tag[] = [];

  allRegistersSelected = false;

  reportInProgress = false;

  displayPrimaryIntegratedCompanyName = false;

  registerPermissionType = RegisterPermissionType;

  get filteredRegisters() {
    let result = this.registers;

    if (this.search) {
      result = filterAndSortByValue(
        result,
        this.search,
        'name',
        'entityName',
        'primaryIntegratedCompanyName',
      );
    }

    if (this.selectedTags.length > 0) {
      // what tags are we actually filtering on
      const filteringTags = this.tags.filter(
        t => t.values.filter(v => this.selectedTags.includes(v.id)).length > 0,
      );
      console.log('filtering tags: ' + JSON.stringify(filteringTags));
      // for each tag that we are filtering on perform an or operation for each value
      // which results to an and between tags to progressively filter
      filteringTags.forEach(t => {
        // applicable tagValueIds
        const applicableTagIds = t.values
          .map(v => v.id)
          .filter(id => this.selectedTags.includes(id));

        // filter to only applicable tagvalueids to correctly perform or/and operations accross/between tags
        result = result.filter(r => {
          const tagValueIds = r.tags.map(tvi => tvi.tagValueId);
          const includedInFilter = tagValueIds.filter(tagValueId =>
            applicableTagIds.includes(tagValueId),
          );
          return includedInFilter.length > 0;
        });
      });
    }

    return result;
  }

  isFiltered() {
    return this.selectedTags.length > 0;
  }

  canFilter() {
    return this.registers.flatMap(r => r.tags || []).length > 0;
  }

  get areAllRegistersSelected() {
    return this.filteredRegisters.every(r => r._selected || r._disabled);
  }

  get areSomeRegistersSelected() {
    return this.filteredRegisters.some(r => r._selected);
  }
  get isOneRegisterSelected() {
    return this.filteredRegisters.filter(r => r._selected).length === 1;
  }

  get isProduction() {
    return environment.production;
  }

  constructor(
    private exportService: ExportService,
    private modalService: ModalService,
    private organisationService: OrganisationService,
    private organisationApi: OrganisationV2Api,
    private toastr: ToastrService,
    private appNavigationService: AppNavigationService,
    private router: Router,
    @Inject(NgbModal) public ngbModal: TypedNgbModal,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('registers' in changes) {
      this.createIntegratedCompanyColumn(this.registers);
      this.allRegistersSelected = false;

      this.organisationService.listTags().subscribe(listTagsResponse => {
        this.tags = listTagsResponse.tags;
      });
    }
  }

  onExportJson(register: Register) {
    this.exportService.exportRegister(register).subscribe();
  }

  onArchiveRegisterClick(register: Register) {
    this.modalService
      .showQuestionModal(
        'Are you sure you want to archive this register? Once archived, you will need to contact technical support to restore it.',
        'Archive Confirmation',
      )
      .pipe(switchMap(() => this.organisationService.deleteRegister(register.id)))
      .subscribe(() => {
        this.toastr.success(`Register ${register.name} has been successfully archived.`);
        this.registers = this.registers.filter(r => r.id !== register.id);
      });
  }

  onRegistersToggled() {
    if (this.allRegistersSelected) {
      // Unselect all
      this.registers.forEach(r => {
        r._selected = false;
        r._disabled = false;
      });
      this.allRegistersSelected = false;
    } else {
      if (!this.areSomeRegistersSelected) {
        // If no registers are selected, select the first
        this.onRegisterToggled(this.filteredRegisters[0]);
      }

      // Select all compatible registers
      this.filteredRegisters.forEach(r => {
        if (!r._disabled) {
          r._selected = true;
        }
      });
      this.allRegistersSelected = true;
    }
  }

  onRegisterToggled(register: RegisterDto & { _selected?: boolean; _disabled?: boolean }) {
    register._selected = !register._selected;

    if (!this.areSomeRegistersSelected) {
      // If no registers selected, enable all registers
      this.filteredRegisters.forEach(r => (r._disabled = false));
      this.allRegistersSelected = false;
    } else {
      if (this.isOneRegisterSelected) {
        // If one register is selected, disable registers which are not compatible for consolidated reports
        this.filteredRegisters.forEach(r => {
          r._disabled = r.currency !== register.currency || r.countryId !== register.countryId;
        });
      }

      this.allRegistersSelected = this.areAllRegistersSelected;
    }
  }

  onReport() {
    this.reportInProgress = true;

    this.sidePanel.open(
      new SidePanelModel(GenerateConsolidatedReportComponent, 'Generate Multi Register Reports', {
        width: 'sm',
      }),
      sidePanel => {
        sidePanel.organisation = this.organisation;
        sidePanel.registers = this.filteredRegisters;
      },
      sidePanel => [
        sidePanel.closePanel.subscribe(requestId => {
          this.sidePanel.close();
          // if a report request as initiated emit the event so that
          // the view will be switched to reports.
          if (requestId) {
            this.reportRequest.emit(requestId);
          }
        }),
        // unsubscribe will be called when this is closed to restore reportInProgress
        {
          unsubscribe: () => {
            this.reportInProgress = false;
          },
        },
      ],
    );
  }

  openCreateRegisterModal() {
    setupModal(this.ngbModal.open(CreateRegisterModalComponent));
  }

  getPrimaryIntegratedCompanyName(register: RegisterDto) {
    return register.integrations.find(i => i.isPrimary)?.companyName || '';
  }

  private createIntegratedCompanyColumn(registers?: RegisterDto[]) {
    this.displayPrimaryIntegratedCompanyName =
      registers != null &&
      registers.length > 0 &&
      registers.some(r => r.primaryIntegratedCompanyName !== null) &&
      this.isAllRegistersPage;
  }

  getTagsDescription(register: RegisterDto) {
    let description = '';
    let tagName = '';
    register.tags.forEach(tag => {
      if (tagName !== tag.tagName && tagName !== '') {
        description = description + ') ';
      }

      if (tagName !== tag.tagName) {
        description = description + `${tag.tagName}: (${tag.tagValueName}`;
      } else if (tagName === tag.tagName) {
        description = description + `, ${tag.tagValueName}`;
      }
      tagName = tag.tagName;
    });
    if (description !== '') {
      description = description + ')';
    }
    return description;
  }

  filterDescription() {
    let description = '';

    this.tags.forEach(tag => {
      const isFilteredByTag =
        tag.values.filter(rtv => this.selectedTags.includes(rtv.id)).length > 0;
      if (isFilteredByTag) {
        description = description + `${tag.name}: (`;

        let firstValue = true;
        tag.values.forEach(value => {
          if (this.selectedTags.includes(value.id)) {
            description = description + (firstValue ? `` : `, `) + `${value.value}`;
            firstValue = false;
          }
        });
        description = description + `) `;
      }
    });
    return description;
  }

  onFilter() {
    setupModal(
      this.ngbModal.open(FilterTagModalComponent),
      modal => {
        modal.tags = this.tags;
        modal.selectedTags = this.selectedTags;
      },
      modal =>
        modal.changed.subscribe(newSelectedTags => {
          this.selectedTags = newSelectedTags;
          this.registers.forEach(r => {
            r._selected = false;
            r._disabled = false;
          });
          this.allRegistersSelected = false;
        }),
    );
  }

  onClearFilter() {
    this.selectedTags = [];
  }

  onLeaveOrganisation() {
    const currentOrg = this.appNavigationService.getActiveOrganisation();
    this.modalService
      .showQuestionModal(
        `Are you sure you want to leave ${currentOrg.name}? Once you leave, you won't be able to view it until an administrator grants you access again.`,
        'Leave Confirmation',
      )
      .subscribe(_ =>
        this.organisationApi.removeSelf(currentOrg.organisationId).subscribe(__ => {
          this.toastr.success(`You have successfully left ${currentOrg.name}.`);
          this.router.navigate(['/']);
        }),
      );
  }
}
